export const LEVEL_NAMES = [
  "Ei", "Schlupf", "Entdecker", "Neugierig", "Wachsend",
  "Stärker", "Entwickelt", "Bereit", "Legendär", "Unaufhaltbar", "Superheld"
] as const;

const MOODS: Record<string, { emoji: string; label: string }> = {
  happy: { emoji: "😄", label: "Fröhlich" },
  tired: { emoji: "😴", label: "Müde" },
  excited: { emoji: "🤩", label: "Aufgeregt" },
  nervous: { emoji: "😬", label: "Nervös" },
  calm: { emoji: "😌", label: "Ruhig" },
  thoughtful: { emoji: "🤔", label: "Nachdenklich" },
  sad: { emoji: "😢", label: "Traurig" },
  energetic: { emoji: "⚡", label: "Energiegeladen" },
  relaxed: { emoji: "😎", label: "Entspannt" },
  restless: { emoji: "😤", label: "Unruhig" },
  curious: { emoji: "🧐", label: "Neugierig" },
  proud: { emoji: "🦁", label: "Stolz" },
  brave: { emoji: "💪", label: "Mutig" },
  creative: { emoji: "🎨", label: "Kreativ" },
  helpful: { emoji: "🤝", label: "Hilfsbereit" },
  patient: { emoji: "🌱", label: "Geduldig" },
  funny: { emoji: "😂", label: "Lustig" },
  careful: { emoji: "🦉", label: "Vorsichtig" },
  determined: { emoji: "🎯", label: "Entschlossen" },
  gentle: { emoji: "💙", label: "Einfühlsam" },
  honest: { emoji: "⭐", label: "Ehrlich" },
  focused: { emoji: "🔍", label: "Konzentriert" }
};

export function normalizedLevel(level: number | null | undefined) {
  return Math.max(0, Math.min(10, level ?? 0));
}

export function monsterImage(level: number | null | undefined) {
  return `/img/monster-levels/level-${normalizedLevel(level)}.png`;
}

// experience_points zählt nur den Rest im aktuellen Level; die Gesamt-XP sind die Schwellen aller erreichten Level plus Rest.
export function totalXp(level: number | null | undefined, experience: number | null | undefined) {
  const current = normalizedLevel(level);
  return 50 * current * (current + 1) + Math.max(0, experience ?? 0);
}

export function levelProgress(level: number | null | undefined, experience: number | null | undefined) {
  const current = normalizedLevel(level);
  const xp = Math.max(0, experience ?? 0);
  const next = Math.min(10, current + 1);
  const needed = (current + 1) * 100;
  return {
    level: current,
    xp,
    stageName: LEVEL_NAMES[current],
    nextName: LEVEL_NAMES[next],
    needed,
    percent: current >= 10 ? 100 : Math.min(100, Math.round(xp / needed * 100))
  };
}

export function moodLabels(value?: string | null) {
  return (value ?? "").split(",").map((item) => item.trim()).filter(Boolean).map((id) => ({
    id,
    emoji: MOODS[id]?.emoji ?? "✨",
    label: MOODS[id]?.label ?? id
  }));
}

export function ratingPresentation(score: number | null | undefined, maximum = 15) {
  if (score == null) return { percent: 0, color: "#9ca3af" };
  const percent = Math.max(0, Math.min(100, Math.round(score / maximum * 100)));
  return { percent, color: percent >= 70 ? "#22c55e" : percent >= 40 ? "#f59e0b" : "#ef4444" };
}

export function moodEmoji(value: number | null | undefined) {
  return ({ 1: "😢", 2: "😕", 3: "😐", 4: "🙂", 5: "😊", 6: "🤩" } as Record<number, string>)[value ?? 0] ?? "–";
}

export function isoWeekKey(dateValue: string) {
  const local = new Date(`${dateValue.slice(0, 10)}T12:00:00`);
  const utc = new Date(Date.UTC(local.getFullYear(), local.getMonth(), local.getDate()));
  utc.setUTCDate(utc.getUTCDate() + 4 - (utc.getUTCDay() || 7));
  const yearStart = new Date(Date.UTC(utc.getUTCFullYear(), 0, 1));
  const week = Math.ceil((((utc.getTime() - yearStart.getTime()) / 86_400_000) + 1) / 7);
  return `${local.getFullYear()}-KW${String(week).padStart(2, "0")}`;
}
